// src/App.js

import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { ThemeProvider } from '@mui/material/styles';
import store from './store';
import theme from './theme';

import ProfilePage from './components/ProfilePage';
import HomePage from './components/HomePage';
import GatedRoute from './components/GatedRoute';
import TranslationDashboard from './components/TranslationDashboard';
import RushShell from './components/RushShell/Shell';
import EditProject from './components/EditProject';
import TranslateProject from './components/TranslateProject';
import ProjectDetails from './components/ProjectDetails';
import TranslationProjects from './components/TranslationProjects';
import AllProjects from './components/RushPages/AllProjects';
import MyQueue from './components/RushPages/MyQueue';
import Inbox from './components/RushPages/Inbox';
import RushProjectDetail from './components/RushPages/ProjectDetail';
import RushNewProject from './components/RushPages/NewProject';
import RushTranslationMemory from './components/RushPages/TranslationMemory';
import RushGlossaries from './components/RushPages/Glossaries';
import RushBrandVoice from './components/RushPages/BrandVoice';
import RushAIQuality from './components/RushPages/AIQuality';
import RushPeople from './components/RushPages/People';
import RushSettings from './components/RushPages/Settings';
import RushAccount from './components/RushPages/Account';
import RushEditor from './components/RushPages/Editor';
import RushPreview from './components/RushPages/Preview';
import RushOnboarding from './components/RushPages/Onboarding';
import RushHelp from './components/RushPages/Help';
import RushReports from './components/RushPages/Reports';
import RushSources from './components/RushPages/Sources';
import RushAutomations from './components/RushPages/Automations';
import RushAdmin from './components/RushPages/Admin';
import NewTranslation from './components/NewTranslation';
import LanguageSettings from './components/LanguageSettings';
import AccountSettings from './components/AccountSettings';
import DashboardHome from './components/DashboardHome';
import { hasAdminPrivileges } from './utils/permissionsClient';
import { loginSuccess, logout, authCheckCompleted } from './slices/authSlice';
import { clearAuthToken, getStoredToken, getStoredTokenData, storeAuthToken } from './utils/authToken';
import Team from './components/TeamPage';
import GlossaryTerms from './components/GlossaryTerms';
import AdminUsersPage from './components/AdminUsersPage';
import PlatformAdminPage from './components/PlatformAdminPage';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import AdminDeepLUsagePage from './components/AdminDeepLUsagePage';
import AdminAIUsagePage from './components/AdminAIUsagePage';
import AdminSystemLogsPage from './components/AdminSystemLogsPage';
import UtilitiesPage from './components/UtilitiesPage';
import ChatPage from './components/ChatPage';
import FileManager from './components/FileManager';
import TaskBoard from './components/TaskBoard';
import ResetPasswordPage from './components/ResetPasswordPage';
import AnalyticsPage from './components/AnalyticsPage';
import AppsMarketplace from './components/AppsMarketplace';
import SupportCenter from './components/SupportCenter';
import AdminSupportManager from './components/AdminSupportManager';
import AdminSupportTickets from './components/AdminSupportTickets';
import JobsBoard from './components/JobsBoard';
import Pricing from './components/Pricing';
import AboutPageWrapper from './components/AboutPageWrapper';
import ContactPageWrapper from './components/ContactPageWrapper';
import FAQPageWrapper from './components/FAQPageWrapper';
import PricingPageWrapper from './components/PricingPageWrapper';
import FeaturesPageWrapper from './components/FeaturesPageWrapper';
import ScrollToTop from './components/ScrollToTop';
import RushCustomersPage from './landing/rush/RushCustomersPage';
import RushFeatureBrandVoice from './landing/rush/RushFeatureBrandVoice';
import RushFeatureAutomations from './landing/rush/RushFeatureAutomations';
import RushFeatureAiQuality from './landing/rush/RushFeatureAiQuality';
import RushFeaturePreview from './landing/rush/RushFeaturePreview';
import RushCareersPage from './landing/rush/RushCareersPage';
import RushRolePage from './landing/rush/RushRolePage';
import RushLanguagesPage from './landing/rush/RushLanguagesPage';
import RushIntegrationsPage from './landing/rush/RushIntegrationsPage';
import RushSecurityPage from './landing/rush/RushSecurityPage';
import RushPrivacyPage from './landing/rush/RushPrivacyPage';
import RushTermsPage from './landing/rush/RushTermsPage';
import RushGdprPage from './landing/rush/RushGdprPage';
import RushSignInPage from './landing/rush/RushSignInPage';
import RushSignUpPage from './landing/rush/RushSignUpPage';
import RushForgotPasswordPage from './landing/rush/RushForgotPasswordPage';
import './landing/rush/rushTheme'; // applies Rush light/dark theme from localStorage on load

import axios from 'axios';

const DashboardIndex = () => <DashboardHome />;

// Protected route for logged-in users
const ProtectedRoute = ({ element: Component }) => {
    const { isAuthenticated, isLoading } = useSelector((state) => state.auth);
    const location = useLocation();

    if (isLoading) {
        return null;
    }

    return isAuthenticated ? (
        <Component />
    ) : (
        <Navigate to="/login" replace state={{ from: location }} />
    );
};

const App = () => {
    const dispatch = useDispatch();
    const { token, user } = useSelector((state) => state.auth);
    const [socket, setSocket] = useState(null);

    // Restore session
    useEffect(() => {
        const savedToken = getStoredToken();
        if (!savedToken) {
            dispatch(authCheckCompleted());
            return;
        }

        axios
            .get('/api/users/me', {
                headers: { Authorization: `Bearer ${savedToken}` },
            })
            .then((res) => {
                dispatch(loginSuccess({ token: savedToken, user: res.data }));
            })
            .catch(() => {
                clearAuthToken();
                dispatch(logout());
            });
    }, [dispatch]);

    // Socket for presence
    useEffect(() => {
        if (!token || !user) return;
        const existing = getStoredTokenData();
        if (!existing || existing.token !== token) {
            storeAuthToken(token, existing?.rememberMe ?? true);
        }
        const sock = io('/', { transports: ['websocket', 'polling'] });
        sock.emit('join', user._id);
        sock.on('statusUpdate', ({ userId, status }) => {
            // handle if needed
        });
        setSocket(sock);
        return () => {
            sock.disconnect();
            setSocket(null);
        };
    }, [token, user]);

    return (
        <ThemeProvider theme={theme}>
            <Router>
                <ScrollToTop />
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPageWrapper />} />
                    <Route path="/contact" element={<ContactPageWrapper />} />
                    <Route path="/faqs" element={<FAQPageWrapper />} />
                    <Route path="/pricing" element={<PricingPageWrapper />} />
                    <Route path="/features" element={<FeaturesPageWrapper />} />
                    <Route path="/features/brand-voice" element={<RushFeatureBrandVoice />} />
                    <Route path="/features/automations" element={<RushFeatureAutomations />} />
                    <Route path="/features/ai-quality" element={<RushFeatureAiQuality />} />
                    <Route path="/features/preview" element={<RushFeaturePreview />} />
                    <Route path="/customers" element={<RushCustomersPage />} />
                    <Route path="/careers" element={<RushCareersPage />} />
                    <Route path="/roles/:slug" element={<RushRolePage />} />
                    <Route path="/languages" element={<RushLanguagesPage />} />
                    <Route path="/integrations" element={<RushIntegrationsPage />} />
                    <Route path="/security" element={<RushSecurityPage />} />
                    <Route path="/privacy" element={<RushPrivacyPage />} />
                    <Route path="/terms" element={<RushTermsPage />} />
                    <Route path="/gdpr" element={<RushGdprPage />} />
                    <Route path="/forgot-password" element={<RushForgotPasswordPage />} />
                    <Route path="/register" element={<RushSignUpPage />} />
                    <Route path="/login" element={<RushSignInPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* Editor — own chrome, no main sidebar */}
                    <Route
                        path="/editor/:id"
                        element={<ProtectedRoute element={RushEditor} />}
                    />
                    <Route
                        path="/onboarding"
                        element={<ProtectedRoute element={RushOnboarding} />}
                    />

                    {/* Protected dashboard and nested */}
                    <Route
                        path="/dashboard/*"
                        element={<ProtectedRoute element={RushShell} />}
                    >
                        <Route path="" element={<DashboardIndex />} />
                        <Route path="projects" element={<AllProjects />} />
                        <Route path="queue" element={<MyQueue />} />
                        <Route path="inbox" element={<Inbox />} />
                        <Route path="translation-memory" element={<RushTranslationMemory />} />
                        <Route path="glossaries" element={<RushGlossaries />} />
                        <Route path="brand-voice" element={<RushBrandVoice />} />
                        <Route path="ai-quality" element={<RushAIQuality />} />
                        <Route path="people" element={<RushPeople />} />
                        <Route path="settings" element={<RushSettings />} />
                        <Route path="account" element={<RushAccount />} />
                        <Route path="preview" element={<RushPreview />} />
                        <Route path="help" element={<RushHelp />} />
                        <Route path="reports" element={<RushReports />} />
                        <Route path="sources" element={<RushSources />} />
                        <Route path="automations" element={<RushAutomations />} />
                        <Route path="admin" element={<RushAdmin />} />
                        <Route path="new-translation" element={<RushNewProject />} />
                        <Route path="new-translation-classic" element={<NewTranslation />} />
                        <Route path="projects/:id/edit" element={<EditProject />} />
                        <Route path="projects/:id" element={<RushProjectDetail />} />
                        <Route path="projects/:id/translate" element={<TranslateProject />} />
                        <Route path="projects/:id/tasks" element={<TaskBoard />} />
                        <Route path="language-settings" element={<LanguageSettings />} />
                        <Route path="account-settings" element={<AccountSettings />} />
                        <Route path="profile" element={<ProfilePage />} />
                        <Route
                            path="team"
                            element={<GatedRoute element={Team} requiredFeature="teamAccess" />}
                        />
                        <Route path="apps" element={<AppsMarketplace />} />
                        <Route path="support" element={<SupportCenter />} />
                        <Route path="support/:articleSlug" element={<SupportCenter />} />
                        <Route
                            path="chat/:userId?"
                            element={<GatedRoute element={() => <ChatPage socket={window.__SOCKET__} />} requiredFeature="chatAccess" />}
                        />
                        <Route path="file-manager" element={<FileManager />} />
                        <Route path="jobs" element={<JobsBoard />} />
                        <Route
                            path="analytics"
                            element={<GatedRoute element={AnalyticsPage} requiredFeature="analytics" />}
                        />
                        <Route path="pricing" element={<Pricing />} />
                        <Route path="glossary/:glossaryId/terms" element={<GlossaryTerms />} />
                        <Route
                            path="admin/deepl-usage"
                            element={<ProtectedAdminRoute element={AdminDeepLUsagePage} />}
                        />
                        <Route
                            path="admin/logs"
                            element={<ProtectedAdminRoute element={AdminSystemLogsPage} />}
                        />
                        <Route
                            path="admin/ai-usage"
                            element={<ProtectedAdminRoute element={AdminAIUsagePage} />}
                        />
                        <Route
                            path="admin/utilities"
                            element={<ProtectedAdminRoute element={UtilitiesPage} />}
                        />
                        <Route
                            path="admin/support"
                            element={<ProtectedAdminRoute element={AdminSupportManager} />}
                        />
                        <Route
                            path="admin/support-tickets"
                            element={<ProtectedAdminRoute element={AdminSupportTickets} />}
                        />
                        <Route
                            path="admin/users"
                            element={<ProtectedAdminRoute element={AdminUsersPage} />}
                        />
                        <Route
                            path="admin/platform"
                            element={<ProtectedAdminRoute element={PlatformAdminPage} />}
                        />
                    </Route>
                </Routes>
            </Router>
        </ThemeProvider>
    );
};

// Wrap App in Redux Provider
const RootApp = () => (
    <Provider store={store}>
        <App />
    </Provider>
);

export default RootApp;
